import { migrateProjectDocument } from '../project/migrateProjectDocument.js'
import {
  MAX_LAYOUT_OFFSET_COUNT,
  type Offset2D,
  type ProjectDocumentV2,
} from '../project/ProjectDocument.js'
import {
  EditorCommandError,
  applyDocumentCommand,
  documentsSemanticallyEqual,
  readEditorCommandType,
  type EditorCommand,
  type DetectedDotInput,
  type EditorViewport,
  type PlaybackFrame,
} from './commands.js'

export type DeepReadonly<Value> =
  Value extends (...arguments_: never[]) => unknown ? Value
    : Value extends readonly (infer Item)[] ? readonly DeepReadonly<Item>[]
      : Value extends object ? { readonly [Key in keyof Value]: DeepReadonly<Value[Key]> }
        : Value

export interface EditorSelection {
  readonly wordIndex: number | null
  readonly letterIndex: number | null
  readonly dotIndex: number | null
}

export interface EditorDetectedDot extends DetectedDotInput {
  readonly index: number
}

export interface EditorTransient {
  readonly viewport: EditorViewport
  readonly playback: PlaybackFrame
  readonly renderingEnabled: boolean
  readonly selection: EditorSelection
  readonly detectedDots: readonly EditorDetectedDot[]
}

export interface EditorHistorySummary {
  readonly canUndo: boolean
  readonly canRedo: boolean
}

export interface EditorSnapshot {
  readonly revision: number
  readonly document: DeepReadonly<ProjectDocumentV2>
  readonly transient: DeepReadonly<EditorTransient>
  readonly history: EditorHistorySummary
}

export type EditorSelector<Value> = (snapshot: EditorSnapshot) => Value

export interface EditorStore {
  getSnapshot(): EditorSnapshot
  subscribe(listener: () => void): () => void
  subscribeSelector<Value>(selector: EditorSelector<Value>, listener: () => void): () => void
  dispatch(command: EditorCommand): boolean
  beginTransaction(label: string): void
  commitTransaction(): boolean
  cancelTransaction(): boolean
  undo(): boolean
  redo(): boolean
}

export interface EditorStoreOptions {
  readonly historyLimit?: number
  readonly historyWeightLimit?: number
}

interface HistoryEntry {
  readonly label: string
  readonly before: ProjectDocumentV2
  readonly after: ProjectDocumentV2
  readonly undoWeight: number
  readonly redoWeight: number
}

interface Transaction {
  readonly label: string
  readonly base: ProjectDocumentV2
}

interface SelectorSubscription {
  readonly selector: EditorSelector<unknown>
  readonly listener: () => void
  selected: unknown
  active: boolean
}

interface NotificationBatch {
  readonly snapshot: EditorSnapshot
  readonly rootListeners: readonly (() => void)[]
  readonly selectorSubscriptions: readonly SelectorSubscription[]
}

export const DEFAULT_HISTORY_LIMIT = 100
export const MAX_HISTORY_LIMIT = 1_000
export const DEFAULT_HISTORY_WEIGHT_LIMIT = 1_000_000
export const MAX_HISTORY_WEIGHT_LIMIT = 10_000_000
export const HISTORY_ENTRY_ROOT_WEIGHT = 16
// Warm V8 measurement retained ~80 bytes per replaced point plus array slot.
// Eight point units plus the array-slot unit keeps one 100k replacement under the 1M default.
export const HISTORY_OFFSET_POINT_WEIGHT = 8
export const MAX_TRANSACTION_LABEL_LENGTH = 256

export class EditorStoreError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'EditorStoreError'
  }
}

function deepFreeze<Value>(value: Value): Value {
  if (typeof value !== 'object' || value === null || Object.isFrozen(value)) return value
  for (const key of Object.keys(value)) {
    deepFreeze((value as Record<string, unknown>)[key])
  }
  return Object.freeze(value)
}

function defaultTransient(): EditorTransient {
  return deepFreeze({
    viewport: { x: 0, y: 0, zoom: 1 },
    playback: { orderAmount: 1, isPlaying: false, direction: 1, phase: 'complete' },
    renderingEnabled: true,
    selection: { wordIndex: null, letterIndex: null, dotIndex: null },
    detectedDots: [],
  })
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function finite(value: unknown, minimum: number, maximum: number, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < minimum || value > maximum) {
    throw new EditorCommandError(`${label} must be finite and within ${minimum}…${maximum}`)
  }
  return value
}

function booleanValue(value: unknown, label: string): boolean {
  if (typeof value !== 'boolean') throw new EditorCommandError(`${label} must be boolean`)
  return value
}

function viewportValue(value: unknown): EditorViewport {
  if (!isRecord(value)) throw new EditorCommandError('Viewport must be an object')
  return Object.freeze({
    x: finite(value.x, -Number.MAX_VALUE, Number.MAX_VALUE, 'Viewport x'),
    y: finite(value.y, -Number.MAX_VALUE, Number.MAX_VALUE, 'Viewport y'),
    zoom: finite(value.zoom, Number.MIN_VALUE, Number.MAX_VALUE, 'Viewport zoom'),
  })
}

function playbackValue(value: unknown): PlaybackFrame {
  if (!isRecord(value)) throw new EditorCommandError('Playback frame must be an object')
  const orderAmount = finite(value.orderAmount, 0, 1, 'Playback order amount')
  const isPlaying = booleanValue(value.isPlaying, 'Playback playing state')
  const direction: unknown = value.direction
  const phase: unknown = value.phase
  if (direction !== 1 && direction !== -1) {
    throw new EditorCommandError('Playback direction must be 1 or -1')
  }
  if (
    phase !== 'moving'
    && phase !== 'holding-order'
    && phase !== 'holding-chaos'
    && phase !== 'complete'
  ) throw new EditorCommandError('Playback phase is invalid')
  return Object.freeze({ orderAmount, isPlaying, direction, phase })
}

function selectionIndex(value: unknown): number | null {
  if (value === null) return null
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0 || value >= 0xffff_ffff) {
    throw new EditorCommandError('Selection index must be null or a non-negative Uint32 owner')
  }
  return value
}

function detectedOwner(value: unknown, label: string): number {
  if (
    typeof value !== 'number'
    || !Number.isSafeInteger(value)
    || value < 0
    || value >= 0xffff_ffff
  ) throw new EditorCommandError(`${label} must be a real Uint32 owner`)
  return value
}

function detectedDotsValue(value: unknown): readonly EditorDetectedDot[] {
  if (!Array.isArray(value)) throw new EditorCommandError('Detected dots must be an array')
  if (value.length > MAX_LAYOUT_OFFSET_COUNT) {
    throw new EditorCommandError(`Detected dot count exceeds ${MAX_LAYOUT_OFFSET_COUNT}`)
  }
  if (value.length === 0) return Object.freeze([])
  const dots = new Array<EditorDetectedDot>(value.length)
  for (let index = 0; index < value.length; index += 1) {
    const candidate: unknown = value[index]
    if (!isRecord(candidate)) throw new EditorCommandError('Detected dot must be an object')
    const width = finite(candidate.width, 0, Number.MAX_VALUE, 'Detected dot width')
    const height = finite(candidate.height, 0, Number.MAX_VALUE, 'Detected dot height')
    dots[index] = Object.freeze({
      index,
      x: finite(candidate.x, -Number.MAX_VALUE, Number.MAX_VALUE, 'Detected dot x'),
      y: finite(candidate.y, -Number.MAX_VALUE, Number.MAX_VALUE, 'Detected dot y'),
      width,
      height,
      relativeX: finite(
        candidate.relativeX, -Number.MAX_VALUE, Number.MAX_VALUE, 'Detected dot relative x',
      ),
      relativeY: finite(
        candidate.relativeY, -Number.MAX_VALUE, Number.MAX_VALUE, 'Detected dot relative y',
      ),
      wordIndex: detectedOwner(candidate.wordIndex, 'Detected dot word index'),
      letterIndex: detectedOwner(candidate.letterIndex, 'Detected dot letter index'),
    })
  }
  return Object.freeze(dots)
}

function sameViewport(left: EditorViewport, right: EditorViewport): boolean {
  return Object.is(left.x, right.x) && Object.is(left.y, right.y) && Object.is(left.zoom, right.zoom)
}

function samePlayback(left: PlaybackFrame, right: PlaybackFrame): boolean {
  return Object.is(left.orderAmount, right.orderAmount)
    && left.isPlaying === right.isPlaying
    && left.direction === right.direction
    && left.phase === right.phase
}

function sameSelection(left: EditorSelection, right: EditorSelection): boolean {
  return left.wordIndex === right.wordIndex
    && left.letterIndex === right.letterIndex
    && left.dotIndex === right.dotIndex
}

function sameDetectedDots(
  left: readonly EditorDetectedDot[],
  right: readonly EditorDetectedDot[],
): boolean {
  if (left.length !== right.length) return false
  for (let index = 0; index < left.length; index += 1) {
    const before = left[index]
    const after = right[index]
    if (
      before === undefined
      || after === undefined
      || !Object.is(before.x, after.x)
      || !Object.is(before.y, after.y)
      || !Object.is(before.width, after.width)
      || !Object.is(before.height, after.height)
      || !Object.is(before.relativeX, after.relativeX)
      || !Object.is(before.relativeY, after.relativeY)
      || before.wordIndex !== after.wordIndex
      || before.letterIndex !== after.letterIndex
    ) return false
  }
  return true
}

interface DirectionalWeights {
  undo: number
  redo: number
}

function offsetDeltaWeights(before: readonly Offset2D[], after: readonly Offset2D[]): DirectionalWeights {
  if (before === after) return { undo: 0, redo: 0 }
  let sharedPointCount = 0
  const sharedLength = Math.min(before.length, after.length)
  for (let index = 0; index < sharedLength; index += 1) {
    if (before[index] === after[index]) sharedPointCount += 1
  }
  return {
    undo: before.length + (before.length - sharedPointCount) * HISTORY_OFFSET_POINT_WEIGHT,
    redo: after.length + (after.length - sharedPointCount) * HISTORY_OFFSET_POINT_WEIGHT,
  }
}

function documentDeltaWeights(before: ProjectDocumentV2, after: ProjectDocumentV2): DirectionalWeights {
  const weights: DirectionalWeights = {
    undo: HISTORY_ENTRY_ROOT_WEIGHT,
    redo: HISTORY_ENTRY_ROOT_WEIGHT,
  }
  for (const [beforeOffsets, afterOffsets] of [
    [before.layout.wordOffsets, after.layout.wordOffsets],
    [before.layout.letterOffsets, after.layout.letterOffsets],
    [before.layout.dotOffsets, after.layout.dotOffsets],
  ] as const) {
    const offsetWeights = offsetDeltaWeights(beforeOffsets, afterOffsets)
    weights.undo += offsetWeights.undo
    weights.redo += offsetWeights.redo
  }
  if (before.text.content !== after.text.content) {
    weights.undo += before.text.content.length
    weights.redo += after.text.content.length
  }
  if (before.text.activeFont !== after.text.activeFont) {
    weights.undo += before.text.activeFont.length
    weights.redo += after.text.activeFont.length
  }

  const beforeSvg = before.particles.customSvg
  const afterSvg = after.particles.customSvg
  if (beforeSvg !== afterSvg) {
    if (beforeSvg !== null) {
      if (beforeSvg.dataUrl !== afterSvg?.dataUrl) weights.undo += beforeSvg.dataUrl.length
      if (beforeSvg.name !== afterSvg?.name) weights.undo += beforeSvg.name.length
    }
    if (afterSvg !== null) {
      if (afterSvg.dataUrl !== beforeSvg?.dataUrl) weights.redo += afterSvg.dataUrl.length
      if (afterSvg.name !== beforeSvg?.name) weights.redo += afterSvg.name.length
    }
  }
  return weights
}

class EditorStoreImplementation implements EditorStore {
  private document: ProjectDocumentV2
  private transient: EditorTransient
  private history: EditorHistorySummary = Object.freeze({ canUndo: false, canRedo: false })
  private snapshot: EditorSnapshot
  private revision = 0
  private readonly rootListeners = new Set<() => void>()
  private readonly selectorSubscriptions = new Set<SelectorSubscription>()
  private readonly undoStack: HistoryEntry[] = []
  private readonly redoStack: HistoryEntry[] = []
  private undoWeight = 0
  private redoWeight = 0
  private transaction: Transaction | null = null
  private readonly notificationQueue: NotificationBatch[] = []
  private notifying = false
  private reducing = false

  constructor(
    initial: unknown,
    private readonly historyLimit: number,
    private readonly historyWeightLimit: number,
  ) {
    this.document = deepFreeze(migrateProjectDocument(initial))
    this.transient = defaultTransient()
    this.snapshot = this.createSnapshot()
  }

  getSnapshot(): EditorSnapshot {
    return this.snapshot
  }

  subscribe(listener: () => void): () => void {
    if (typeof listener !== 'function') throw new TypeError('Store listener must be a function')
    this.rootListeners.add(listener)
    let active = true
    return () => {
      if (!active) return
      active = false
      this.rootListeners.delete(listener)
    }
  }

  subscribeSelector<Value>(selector: EditorSelector<Value>, listener: () => void): () => void {
    if (typeof selector !== 'function' || typeof listener !== 'function') {
      throw new TypeError('Selector subscription requires functions')
    }
    const subscription: SelectorSubscription = {
      selector: selector as EditorSelector<unknown>,
      listener,
      selected: selector(this.snapshot),
      active: true,
    }
    this.selectorSubscriptions.add(subscription)
    return () => {
      if (!subscription.active) return
      subscription.active = false
      this.selectorSubscriptions.delete(subscription)
    }
  }

  dispatch(command: EditorCommand): boolean {
    this.assertNotReducing()
    let type: string
    let nextDocument: ProjectDocumentV2 | null = null
    let nextTransient: EditorTransient | null = null
    this.reducing = true
    try {
      type = readEditorCommandType(command)
      if (type.startsWith('document/')) {
        nextDocument = applyDocumentCommand(this.document, command, type)
      } else {
        nextTransient = this.applyTransientCommand(command, type)
      }
    } finally {
      this.reducing = false
    }

    if (nextDocument !== null) {
      if (nextDocument === this.document) return false
      const nextRevision = this.nextRevision()
      const before = this.document
      if (this.transaction === null) {
        this.clearRedo()
        this.pushUndo(this.historyEntry(type, before, nextDocument))
        this.refreshHistorySummary()
      }
      this.document = nextDocument
      this.publish(nextRevision)
      return true
    }
    if (nextTransient === null || nextTransient === this.transient) return false
    const nextRevision = this.nextRevision()
    this.transient = nextTransient
    this.publish(nextRevision)
    return true
  }

  beginTransaction(label: string): void {
    this.assertNotReducing()
    if (this.transaction !== null) throw new EditorStoreError('Nested editor transactions are not allowed')
    if (
      typeof label !== 'string'
      || label.length === 0
      || label.length > MAX_TRANSACTION_LABEL_LENGTH
    ) {
      throw new EditorStoreError(
        `Transaction label must contain 1…${MAX_TRANSACTION_LABEL_LENGTH} code units`,
      )
    }
    this.assertRevisionCapacity()
    this.transaction = Object.freeze({ label, base: this.document })
  }

  commitTransaction(): boolean {
    this.assertNotReducing()
    const transaction = this.transaction
    if (transaction === null) return false
    this.assertRevisionCapacity()
    if (documentsSemanticallyEqual(transaction.base, this.document)) {
      this.transaction = null
      return false
    }
    const nextRevision = this.nextRevision()
    const entry = this.historyEntry(transaction.label, transaction.base, this.document)
    this.transaction = null
    this.clearRedo()
    this.pushUndo(entry)
    if (this.refreshHistorySummary()) this.publish(nextRevision)
    return true
  }

  cancelTransaction(): boolean {
    this.assertNotReducing()
    const transaction = this.transaction
    if (transaction === null) return false
    this.assertRevisionCapacity()
    if (this.document === transaction.base) {
      this.transaction = null
      return false
    }
    const nextRevision = this.nextRevision()
    this.document = transaction.base
    this.transaction = null
    this.publish(nextRevision)
    return true
  }

  undo(): boolean {
    this.assertNotReducing()
    this.assertNoTransaction('undo')
    const entry = this.undoStack[this.undoStack.length - 1]
    if (entry === undefined) return false
    const nextRevision = this.nextRevision()
    this.undoStack.pop()
    this.undoWeight -= entry.undoWeight
    this.pushRedo(entry)
    this.document = entry.before
    this.refreshHistorySummary()
    this.publish(nextRevision)
    return true
  }

  redo(): boolean {
    this.assertNotReducing()
    this.assertNoTransaction('redo')
    const entry = this.redoStack[this.redoStack.length - 1]
    if (entry === undefined) return false
    const nextRevision = this.nextRevision()
    this.redoStack.pop()
    this.redoWeight -= entry.redoWeight
    this.pushUndo(entry, true)
    this.document = entry.after
    this.refreshHistorySummary()
    this.publish(nextRevision)
    return true
  }

  private assertNotReducing(): void {
    if (this.reducing) throw new EditorStoreError('Editor store mutations are not allowed during command reduction')
  }

  private assertRevisionCapacity(): void {
    if (!Number.isSafeInteger(this.revision) || this.revision >= Number.MAX_SAFE_INTEGER) {
      throw new EditorStoreError('Editor snapshot revision is exhausted')
    }
  }

  private nextRevision(): number {
    this.assertRevisionCapacity()
    return this.revision + 1
  }

  private assertNoTransaction(operation: string): void {
    if (this.transaction !== null) throw new EditorStoreError(`Cannot ${operation} during an editor transaction`)
  }

  private pushUndo(entry: HistoryEntry, preserveNewest = false): void {
    this.undoStack.push(entry)
    this.undoWeight += entry.undoWeight
    while (this.undoStack.length > this.historyLimit) this.evictOldestUndo()
    this.trimHistoryWeight(preserveNewest ? 'redo' : 'undo')
  }

  private pushRedo(entry: HistoryEntry): void {
    this.redoStack.push(entry)
    this.redoWeight += entry.redoWeight
    while (this.redoStack.length > this.historyLimit) this.evictOldestRedo()
    this.trimHistoryWeight('undo')
  }

  private trimHistoryWeight(first: 'undo' | 'redo'): void {
    while (this.undoWeight + this.redoWeight > this.historyWeightLimit) {
      const evicted = first === 'undo'
        ? this.evictOldestUndo() || this.evictOldestRedo()
        : this.evictOldestRedo() || this.evictOldestUndo()
      if (!evicted) break
    }
  }

  private evictOldestUndo(): boolean {
    const evicted = this.undoStack.shift()
    if (evicted === undefined) return false
    this.undoWeight -= evicted.undoWeight
    return true
  }

  private evictOldestRedo(): boolean {
    const evicted = this.redoStack.shift()
    if (evicted === undefined) return false
    this.redoWeight -= evicted.redoWeight
    return true
  }

  private clearRedo(): void {
    this.redoStack.length = 0
    this.redoWeight = 0
  }

  private historyEntry(label: string, before: ProjectDocumentV2, after: ProjectDocumentV2): HistoryEntry {
    const weights = documentDeltaWeights(before, after)
    return Object.freeze({
      label,
      before,
      after,
      undoWeight: weights.undo,
      redoWeight: weights.redo,
    })
  }

  private refreshHistorySummary(): boolean {
    const canUndo = this.undoStack.length > 0
    const canRedo = this.redoStack.length > 0
    if (this.history.canUndo === canUndo && this.history.canRedo === canRedo) return false
    this.history = Object.freeze({ canUndo, canRedo })
    return true
  }

  private createSnapshot(): EditorSnapshot {
    return Object.freeze({
      revision: this.revision,
      document: this.document,
      transient: this.transient,
      history: this.history,
    })
  }

  private publish(nextRevision: number): void {
    this.revision = nextRevision
    this.snapshot = this.createSnapshot()
    this.notificationQueue.push(Object.freeze({
      snapshot: this.snapshot,
      rootListeners: Object.freeze([...this.rootListeners]),
      selectorSubscriptions: Object.freeze([...this.selectorSubscriptions]),
    }))
    if (this.notifying) return
    this.notifying = true
    try {
      while (this.notificationQueue.length > 0) {
        const batch = this.notificationQueue.shift()
        if (batch === undefined) continue
        for (const listener of batch.rootListeners) {
          try { listener() } catch { /* Observers cannot roll back committed store state. */ }
        }
        for (const subscription of batch.selectorSubscriptions) {
          if (!subscription.active) continue
          try {
            const selected = subscription.selector(batch.snapshot)
            if (Object.is(selected, subscription.selected)) continue
            subscription.selected = selected
            subscription.listener()
          } catch {
            // A failing selector/observer cannot prevent independent subscriptions.
          }
        }
      }
    } finally {
      this.notifying = false
    }
  }

  private applyTransientCommand(command: unknown, type: string): EditorTransient {
    if (!isRecord(command)) {
      throw new EditorCommandError('Editor command must be an object')
    }
    switch (type) {
      case 'transient/set-viewport': {
        const viewport = viewportValue(command.value)
        if (sameViewport(this.transient.viewport, viewport)) return this.transient
        return Object.freeze({ ...this.transient, viewport })
      }
      case 'transient/reset-viewport': {
        const viewport = Object.freeze({ x: 0, y: 0, zoom: 1 })
        if (sameViewport(this.transient.viewport, viewport)) return this.transient
        return Object.freeze({ ...this.transient, viewport })
      }
      case 'transient/set-playback-frame': {
        const playback = playbackValue(command.value)
        if (samePlayback(this.transient.playback, playback)) return this.transient
        return Object.freeze({ ...this.transient, playback })
      }
      case 'transient/set-playing': {
        const isPlaying = booleanValue(command.value, 'Playback playing state')
        if (this.transient.playback.isPlaying === isPlaying) return this.transient
        const playback = Object.freeze({ ...this.transient.playback, isPlaying })
        return Object.freeze({ ...this.transient, playback })
      }
      case 'transient/set-rendering-enabled': {
        const renderingEnabled = booleanValue(command.value, 'Rendering enabled state')
        if (this.transient.renderingEnabled === renderingEnabled) return this.transient
        return Object.freeze({ ...this.transient, renderingEnabled })
      }
      case 'transient/set-detected-dots': {
        const candidate = detectedDotsValue(command.value)
        const dotsChanged = !sameDetectedDots(this.transient.detectedDots, candidate)
        const dotIndex = this.transient.selection.dotIndex
        const selectionChanged = dotIndex !== null && dotIndex >= candidate.length
        if (!dotsChanged && !selectionChanged) return this.transient
        const detectedDots = dotsChanged ? candidate : this.transient.detectedDots
        const selection = selectionChanged
          ? Object.freeze({ ...this.transient.selection, dotIndex: null })
          : this.transient.selection
        return Object.freeze({ ...this.transient, detectedDots, selection })
      }
      case 'transient/set-selection': {
        const rawIndex: unknown = command.index
        const target: unknown = command.target
        const index = selectionIndex(rawIndex)
        const key = target === 'word' ? 'wordIndex'
          : target === 'letter' ? 'letterIndex'
            : target === 'dot' ? 'dotIndex'
              : null
        if (key === null) throw new EditorCommandError('Selection target is invalid')
        if (this.transient.selection[key] === index) return this.transient
        const selection = Object.freeze({ ...this.transient.selection, [key]: index })
        return Object.freeze({ ...this.transient, selection })
      }
      case 'transient/clear-selection': {
        const selection = Object.freeze({ wordIndex: null, letterIndex: null, dotIndex: null })
        if (sameSelection(this.transient.selection, selection)) return this.transient
        return Object.freeze({ ...this.transient, selection })
      }
      default: throw new EditorCommandError('Editor command type is invalid')
    }
  }
}

export function createEditorStore(initial?: unknown, options: EditorStoreOptions = {}): EditorStore {
  const historyLimit = options.historyLimit ?? DEFAULT_HISTORY_LIMIT
  if (!Number.isSafeInteger(historyLimit) || historyLimit < 1 || historyLimit > MAX_HISTORY_LIMIT) {
    throw new EditorStoreError(`History limit must be a positive safe integer no greater than ${MAX_HISTORY_LIMIT}`)
  }
  const historyWeightLimit = options.historyWeightLimit ?? DEFAULT_HISTORY_WEIGHT_LIMIT
  if (
    !Number.isSafeInteger(historyWeightLimit)
    || historyWeightLimit < 1
    || historyWeightLimit > MAX_HISTORY_WEIGHT_LIMIT
  ) {
    throw new EditorStoreError(
      `History weight limit must be a positive safe integer no greater than ${MAX_HISTORY_WEIGHT_LIMIT}`,
    )
  }
  return new EditorStoreImplementation(initial, historyLimit, historyWeightLimit)
}
